<template>
  <div class="cabinet-page" :class="{ 'is-loading': loading }">
    <app-cells><h1 class="title">{{ $t('advances.title') }}</h1></app-cells>
    <app-button class="btn--stretch" theme="request" @click.native="showRequestModal">{{ $t('advances.request') }}</app-button>

    <app-page-loader v-if="loading" />
    <cabinet-state
      v-else-if="loadError"
      class="advance-empty"
      type="error"
      :title="$t('advances.unavailable')"
      :message="$t('common.pleaseRetry')"
      @retry="loadAdvances"
    />
    <template v-else>
      <section v-if="currentAdvances.length" class="advance-group">
        <h2 class="advance-group__title advance-group__title--current">{{ $t('common.current') }}</h2>
        <advance-card v-for="advance in currentAdvances" :key="advance.id" :advance="advance" @cancel="showCancelModal" />
      </section>
      <section v-if="historyAdvances.length" class="advance-group">
        <h2 class="advance-group__title advance-group__title--history">{{ $t('common.history') }}</h2>
        <advance-card v-for="advance in historyAdvances" :key="advance.id" :advance="advance" />
      </section>
      <cabinet-state
        v-if="!advances.length"
        class="advance-empty"
        type="empty"
        :title="$t('common.noRequests')"
        :message="$t('common.requestsAppear')"
      />
    </template>

    <modal
      name="request-advance"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      :shiftY="0"
      transition="pop-out"
    >
      <div class="popup">
        <button @click="$modal.hide('request-advance')" class="popup__close" type="button" :aria-label="$t('common.close')"></button>
        <div class="popup__title">{{ $t('advances.newRequest') }}</div>
        <div class="popup__body">
          <div class="popup__info">{{ $t('advances.monthlyLimit') }}</div>
          <advance-request-form :key="requestFormKey" @created="onCreated" />
        </div>
      </div>
    </modal>

    <modal
      name="cancel-advance"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      :shiftY="0"
      transition="pop-out"
    >
      <div class="popup">
        <button @click="$modal.hide('cancel-advance')" class="popup__close" type="button" :aria-label="$t('common.close')"></button>
        <div class="popup__title">{{ $t('advances.cancelTitle') }}</div>
        <div class="popup__body">
          <div class="popup__info">{{ $t('advances.moveHistory') }}</div>
          <app-cells position="center" :indent="false" :wrap="false">
            <app-button theme="dark" @click.native="$modal.hide('cancel-advance')">{{ $t('common.keepRequest') }}</app-button>
            <app-button ref="cancel" theme="error" :disabled="cancelling" @click.native="cancelAdvance">
              {{ $t('common.cancelRequest') }}
            </app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { cancelPersonalCabinetAdvance, getPersonalCabinetAdvances } from '@/http'
import AdvanceCard from './AdvanceCard'
import AdvanceRequestForm from './AdvanceRequestForm'
import CabinetState from './CabinetState'

const HISTORY_STATUSES = ['rejected', 'cancelled', 'repaid']

export default {
  name: 'PersonalCabinetAdvances',
  components: { AdvanceCard, AdvanceRequestForm, CabinetState },
  data() {
    return {
      loading: true,
      loadError: false,
      advances: [],
      requestFormKey: 0,
      cancelling: false,
      advanceToCancel: null,
    }
  },
  computed: {
    currentAdvances() {
      return this.advances.filter(advance => !HISTORY_STATUSES.includes(advance.status.code))
    },
    historyAdvances() {
      return this.advances.filter(advance => HISTORY_STATUSES.includes(advance.status.code))
    },
  },
  created() {
    this.loadAdvances()
  },
  methods: {
    loadAdvances() {
      this.loading = true
      this.loadError = false
      return getPersonalCabinetAdvances()
        .then(response => {
          this.advances = response.data
        })
        .catch(() => {
          this.loadError = true
        })
        .finally(() => {
          this.loading = false
        })
    },
    showRequestModal() {
      this.requestFormKey += 1
      this.$modal.show('request-advance')
    },
    onCreated() {
      this.$modal.hide('request-advance')
      this.$root.$emit('personal-cabinet:requests-changed')
      this.loadAdvances()
    },
    showCancelModal(advance) {
      this.advanceToCancel = advance
      this.$modal.show('cancel-advance')
    },
    cancelAdvance() {
      if (!this.advanceToCancel || this.cancelling) return
      this.cancelling = true
      this.$refs.cancel.preload = true
      cancelPersonalCabinetAdvance(this.advanceToCancel.id)
        .then(response => {
          const index = this.advances.findIndex(advance => advance.id === response.data.id)
          if (index !== -1) this.$set(this.advances, index, response.data)
          this.$modal.hide('cancel-advance')
          this.advanceToCancel = null
          this.$root.$emit('personal-cabinet:requests-changed')
        })
        .finally(() => {
          this.cancelling = false
          if (this.$refs.cancel) this.$refs.cancel.preload = false
        })
    },
  },
}
</script>

<style lang="sass" scoped>
.advance-group
  margin-top: 26px

.advance-group__title
  margin-bottom: 12px
  padding: 9px 12px
  border-left: 4px solid
  border-radius: 0 8px 8px 0
  font-size: 14px
  font-weight: 600
  letter-spacing: 1px
  text-transform: uppercase

.advance-group__title--current
  border-color: $color-theme
  background-color: #f1f7e9
  color: #4f8d12

.advance-group__title--history
  border-color: #8a8a8a
  background-color: #f3f3f3
  color: #5f5f5f

.advance-empty
  margin-top: 24px

</style>
